import { AudioPlayer, Button, TableItem, TableMessage } from "components/atoms";
import { FormInput, MagellanPopups } from "components/molecules";
import ImportArtistsAndTracksPopup from "components/molecules/popup/import-artists-and-tracks/ImportArtistsAndTracksPopup";
import { Table } from "components/organisms";
import { useFormikContext } from "formik";
import { IFormikContextType } from "hooks/useFormikField";
import i18next from "i18next";
import middleware from "middleware";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  PiCaretCircleDown,
  PiCaretCircleLeft,
  PiCaretCircleRight,
  PiCaretCircleUp,
  PiDownload,
  PiNavigationArrow,
  PiPlus,
  PiTrash,
} from "react-icons/pi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PopupActions } from "reactjs-popup/dist/types";
import { IMusicProductPhysicalForm } from "services/music-product-physical/types/form";
import { useGetTrackLightList } from "services/tracks/light";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { array, number, object, string } from "yup";

const tracksValidation = object().shape({
  id: string().required(() => i18next.t("formik-error.required")),
  name: string(),
});

export const albumValidation = object().shape({
  albumNumber: number().required(() => i18next.t("formik-error.required")),
  tracks: array()
    .of(tracksValidation)
    .min(1, () => i18next.t("formik-error.at-least-one-track-required"))
    .required(() => i18next.t("formik-error.required")),
});

const trackInitialValue = {
  name: "",
  id: "",
  isrc: "",
  mp3Link: "",
  mp3PreviewLink: "",
  wavLink: "",
  flacLink: "",
};

const MusicProductPhysicalTracks = () => {
  const {
    values,
    status,
    setFieldValue,
    errors,
    initialValues,
    resetForm,
  }: IFormikContextType<IMusicProductPhysicalForm> =
    useFormikContext<IMusicProductPhysicalForm>();
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tracksLightList = useGetTrackLightList();

  const allTracksId = values.tracks
    .map((album) => album.tracks.map((track) => track.id))
    .flat();

  const trackSelectOptions =
    tracksLightList.data
      ?.map((track) => ({
        label: track.mainTitle || "unknown name",
        value: track.id,
      }))
      .filter((track) => !allTracksId.includes(track.value)) ?? [];

  const albumNumber = searchParams.get("album");

  const selectedAlbum = albumNumber ? parseInt(albumNumber) : 0;

  const albumIndex = values.tracks.findIndex(
    (album) => album.albumNumber === selectedAlbum
  );

  const album = values.tracks.find(
    (album) => album.albumNumber === selectedAlbum
  );

  const albumTracksLength = album?.tracks.length;

  const areAllTracksSelected = !allTracksId.includes("");

  const getAlbumTrackIndex = (id: string) =>
    values.tracks[albumIndex].tracks.findIndex((track) => track.id === id);

  const onGoToAlbum = (number: number) => setSearchParams(`album=${number}`);

  const goToTrack = (id: string) => window.open(`/track/${id}/file`, '_blank');

  const onAddAlbum = () => {
    const tracksLength = values.tracks.length;

    setFieldValue(`tracks[${tracksLength}]`, {
      albumNumber: tracksLength + 1,
      tracks: [],
    });

    onGoToAlbum(tracksLength + 1);
  };

  const onRemoveAlbum = (popupActions?: PopupActions) => {
    setFieldValue(
      `tracks`,
      values.tracks
        .filter((album) => album.albumNumber !== selectedAlbum)
        .map((album, index) => ({
          albumNumber: index + 1,
          tracks: album.tracks,
        }))
    );

    onGoToAlbum(selectedAlbum - 1);

    popupActions?.close();
  };

  const onAddTrackToAlbum = () => {
    setFieldValue(
      `tracks[${albumIndex}].tracks[${albumTracksLength}]`,
      trackInitialValue
    );
  };

  const onAddTrackAboveToAlbum = (id: number) => {
    let tracks = values.tracks[albumIndex].tracks;
    tracks.splice(id, 0, trackInitialValue);
    setFieldValue(`tracks[${albumIndex}].tracks`, tracks);
  };

  const onAddTrackUnderToAlbum = (id: number) => {
    let tracks = values.tracks[albumIndex].tracks;
    tracks.splice(id + 1, 0, trackInitialValue);
    setFieldValue(`tracks[${albumIndex}].tracks`, tracks);
  };

  const onRemoveTrackFromAlbum = (id: string) => {
    setFieldValue(
      `tracks[${albumIndex}].tracks`,
      album?.tracks.filter((track) => track.id !== id) ?? []
    );
  };

  const onMoveTrackUp = (id: string) => {
    const trackIndex = getAlbumTrackIndex(id);
    const moveingTrack = values.tracks[albumIndex].tracks[trackIndex];
    setFieldValue(
      `tracks[${albumIndex}].tracks[${trackIndex}]`,
      values.tracks[albumIndex].tracks[trackIndex + 1]
    );
    setFieldValue(
      `tracks[${albumIndex}].tracks[${trackIndex + 1}]`,
      moveingTrack
    );
  };

  const onMoveTrackDown = (id: string) => {
    const trackIndex = getAlbumTrackIndex(id);
    const moveingTrack = values.tracks[albumIndex].tracks[trackIndex];
    setFieldValue(
      `tracks[${albumIndex}].tracks[${trackIndex}]`,
      values.tracks[albumIndex].tracks[trackIndex - 1]
    );
    setFieldValue(
      `tracks[${albumIndex}].tracks[${trackIndex - 1}]`,
      moveingTrack
    );
  };

  const onMoveAlbumLeft = () => {
    if (albumIndex === 0) return;

    const albumIndexTracks = values.tracks[albumIndex];
    const previousAlbumIndexTracks = values.tracks[albumIndex - 1];

    setFieldValue(`tracks[${albumIndex}]`, {
      ...previousAlbumIndexTracks,
      albumNumber: albumIndex + 1,
    });
    setFieldValue(`tracks[${albumIndex - 1}]`, {
      ...albumIndexTracks,
      albumNumber: albumIndex,
    });

    onGoToAlbum(albumIndex);
  };

  const onMoveAlbumRight = () => {
    if (albumIndex === values.tracks.length - 1) return;

    const albumIndexTracks = values.tracks[albumIndex];
    const previousAlbumIndexTracks = values.tracks[albumIndex + 1];

    setFieldValue(`tracks[${albumIndex}]`, {
      ...previousAlbumIndexTracks,
      albumNumber: albumIndex + 1,
    });
    setFieldValue(`tracks[${albumIndex + 1}]`, {
      ...albumIndexTracks,
      albumNumber: albumIndex + 2,
    });

    onGoToAlbum(albumIndex + 2);
  };

  const albumHasError = (key: number) =>
    errors.tracks && errors.tracks[key] !== undefined;

  const onDownloadAudio = () => {
    toast.success(t("toast-message.audio-downloaded-successfully"));
  };

  useEffect(() => {
    if (initialValues.tracks.length) {
      setSearchParams(`album=1`);
    }
  }, [initialValues.tracks.length]);

  return (
    <div className="flex flex-col w-full h-full gap-2">
      {!values.tracks.length ? (
        status?.displayMode ? (
          <div className="flex w-full h-full justify-center items-center">
            {t("music-product-physical.tracks.no-album-yet")}
          </div>
        ) : (
          <div className="flex w-full justify-between items-center gap-2">
            <Button variant={BUTTON_VARIANT.white} onClick={onAddAlbum}>
              <Button.Text
                text={t("music-product-physical.tracks.add-first-album")}
              />
              <Button.Icon icon={<PiPlus />} />
            </Button>
            {!status?.displayMode && productId && (
              <ImportArtistsAndTracksPopup
                direction="fromDigital"
                targetProductId={productId}
                version={values.version}
                onPhysicalSuccess={(data) => {
                  resetForm({
                    values: middleware.musicProductPhysical.queryToForm(data),
                  });
                }}
              />
            )}
          </div>
        )
      ) : (
        <div className="flex gap-2 w-full justify-between items-start">
          <div className="flex gap-2 w-full">
            {values.tracks.map((track, key) => (
              <Button
                key={key}
                buttonStyle={
                  selectedAlbum !== track.albumNumber && !!albumHasError(key)
                    ? { color: "red" }
                    : {}
                }
                variant={BUTTON_VARIANT.white}
                isActive={selectedAlbum === track.albumNumber}
                onClick={() => onGoToAlbum(track.albumNumber)}
              >
                <Button.Text
                  text={t("music-product-physical.tracks.album", {
                    number: track.albumNumber,
                  })}
                />
              </Button>
            ))}
            {status?.displayMode ? null : (
              <Button
                variant={BUTTON_VARIANT.white}
                disabled={
                  albumIndex !== -1 &&
                  !values.tracks[values.tracks.length - 1].tracks?.length &&
                  areAllTracksSelected
                }
                onClick={onAddAlbum}
              >
                <Button.Icon icon={<PiPlus />} />
              </Button>
            )}
          </div>
          {!status?.displayMode && (
            <div className="flex gap-2 shrink-0 items-start">
              {productId && (
                <ImportArtistsAndTracksPopup
                  direction="fromDigital"
                  targetProductId={productId}
                  version={values.version}
                  onPhysicalSuccess={(data) => {
                    resetForm({
                      values: middleware.musicProductPhysical.queryToForm(data),
                    });
                    if (data.tracks?.length) {
                      setSearchParams("album=1");
                    }
                  }}
                />
              )}
              {albumIndex !== -1 && (
                <>
                  <div className="flex gap-[1px]">
                    <Button
                      variant={BUTTON_VARIANT.white}
                      onlyIcon
                      buttonStyle={{
                        borderTopRightRadius: 2,
                        borderBottomRightRadius: 2,
                      }}
                      disabled={albumIndex === 0}
                      onClick={onMoveAlbumLeft}
                    >
                                            <Button.Description description={t('button.previous')} />

                                            <Button.Icon icon={<PiCaretCircleLeft />} />
                    </Button>
                    <Button
                      variant={BUTTON_VARIANT.white}
                      onlyIcon
                      disabled={albumIndex === values.tracks.length - 1}
                      buttonStyle={{
                        borderTopLeftRadius: 2,
                        borderBottomLeftRadius: 2,
                      }}
                      onClick={onMoveAlbumRight}
                    >
                                            <Button.Description description={t('button.next-item')} />

                                            <Button.Icon icon={<PiCaretCircleRight />} />
                    </Button>
                  </div>
                  {values.tracks[albumIndex].tracks.length >= 2 ? (
                    <MagellanPopups.DeleteWarning
                      name={t("music-product-physical.tracks.album", {
                        number: selectedAlbum,
                      })}
                      transI18nKey="music-product-physical.tracks.delete-message"
                      onRemove={onRemoveAlbum}
                      isClear={false}
                    />
                  ) : (
                    <Button
                      variant={BUTTON_VARIANT.remove}
                      onlyIcon
                      onClick={() => onRemoveAlbum()}
                    >
                                            <Button.Description description={t('button.remove')} />

                                            <Button.Icon icon={<PiTrash />} />
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {!selectedAlbum || albumIndex === -1 ? null : (
        <Table
          size="compact"
          data={{
            nodes:
              album?.tracks.map((track, key) => ({
                trackName: track.name,
                trackId: track.id,
                isrc: track.isrc,
                id: key,
                mp3PreviewLink: track.mp3PreviewLink,
                mp3Link: track.mp3Link,
                wavLink: track.wavLink,
                flacLink: track.flacLink,
              })) ?? [],
          }}
          columns={
            status?.displayMode
              ? [
                  {
                    label: t("music-product-physical.tracks.header", {
                      number: albumIndex + 1,
                    }),
                    renderCell: ({ trackId, trackName, id }) => (
                      <TableItem>
                        <TableItem.Text
                          text={`${id + 1}.  ${trackName ?? "No name"}`}
                        />
                      </TableItem>
                    ),
                  },
                  {
                    label: t("input.label.isrc"),
                    renderCell: ({ isrc }) => (
                      <TableItem>
                        <TableItem.Text text={isrc} />
                      </TableItem>
                    ),
                  },
                  {
                    label: "",
                    renderCell: ({ trackId, mp3PreviewLink }) => (
                      <TableItem>
                        <AudioPlayer
                          trackId={trackId}
                          url={mp3PreviewLink}
                          disabled={!mp3PreviewLink}
                        />
                      </TableItem>
                    ),
                  },
                  {
                    label: "",
                    renderCell: ({ trackId, mp3Link, wavLink, flacLink }) => (
                      <TableItem justifyPosition="end">
                        <div className="flex gap-1">
                          {mp3Link && (
                            <a href={mp3Link} onClick={onDownloadAudio}>
                              <Button isClear onlyIcon>
                                <Button.Text text={`.mp3`} />
                                                                <Button.Description description={t('button.download')} />

                                                                <Button.Icon icon={<PiDownload />} />
                              </Button>
                            </a>
                          )}
                          {wavLink && (
                            <a href={wavLink} onClick={onDownloadAudio}>
                              <Button isClear onlyIcon>
                                <Button.Text text={`.wav`} />
                                                                <Button.Description description={t('button.download')} />

                                                                <Button.Icon icon={<PiDownload />} />
                              </Button>
                            </a>
                          )}
                          {flacLink && (
                            <a href={flacLink} onClick={onDownloadAudio}>
                              <Button isClear onlyIcon>
                                <Button.Text text={`.wav`} />
                                                                <Button.Description description={t('button.download')} />

                                                                <Button.Icon icon={<PiDownload />} />
                              </Button>
                            </a>
                          )}
                          <Button
                            isClear
                            onlyIcon
                            onClick={() => goToTrack(trackId)}
                          >
                            <Button.Description description={t('button.open')} />
                            <Button.Icon
                              icon={
                                <PiNavigationArrow transform="scale(-1,1)" />
                              }
                            />
                          </Button>
                        </div>
                      </TableItem>
                    ),
                    resize: true,
                  },
                ]
              : [
                  {
                    label: t("music-product-physical.tracks.header", {
                      number: albumIndex + 1,
                    }),
                    renderCell: ({ trackId, trackName, id }) => (
                      <TableItem>
                        <div className="flex gap-5 w-full items-center">
                          {id + 1}.
                          <FormInput.Select
                            name={`tracks[${albumIndex}].tracks[${id}].id`}
                            isAsync
                            isSearchable
                            label={t(`input.label.track`)}
                            onChange={(value, actionMeta) => {
                              setFieldValue(
                                `tracks[${albumIndex}].tracks[${id}].name`,
                                value.label
                              );
                            }}
                            value={{
                              label: trackName,
                              value: trackId,
                            }}
                            options={trackSelectOptions}
                            isLoading={tracksLightList.isLoading}
                          />
                        </div>
                      </TableItem>
                    ),
                  },
                  {
                    label: "",
                    renderCell: ({ trackId, id }) => (
                      <TableItem justifyPosition="center">
                        <TableItem.AddButtonsWrapper>
                          <TableItem.AddButton
                            disable={!areAllTracksSelected}
                            onClick={() => onAddTrackAboveToAlbum(id)}
                          />
                          {id + 1 !== albumTracksLength && (
                            <TableItem.AddButton
                              disable={!areAllTracksSelected}
                              position="bottom"
                              onClick={() => onAddTrackUnderToAlbum(id)}
                            />
                          )}
                        </TableItem.AddButtonsWrapper>
                      </TableItem>
                    ),
                  },
                  {
                    label: "",
                    renderCell: ({ trackId, id }) => (
                      <TableItem justifyPosition="end">
                        <div className="flex gap-4">
                          <div className="flex">
                            <Button
                              onlyIcon
                              onClick={() => onMoveTrackUp(trackId)}
                              disabled={id + 1 === albumTracksLength}
                              isClear
                            >
                                                            <Button.Description description={t('button.move-down')} />

                                                            <Button.Icon icon={<PiCaretCircleDown />} />
                            </Button>
                            <Button
                              onlyIcon
                              disabled={id === 0}
                              onClick={() => onMoveTrackDown(trackId)}
                              isClear
                            >
                                                            <Button.Description description={t('button.move-up')} />

                                                            <Button.Icon icon={<PiCaretCircleUp />} />
                            </Button>
                          </div>
                          <Button
                            variant={BUTTON_VARIANT.remove}
                            onlyIcon
                            onClick={() => onRemoveTrackFromAlbum(trackId)}
                            isClear
                          >
                                                        <Button.Description description={t('button.remove')} />

                                                        <Button.Icon icon={<PiTrash />} />
                          </Button>
                        </div>
                      </TableItem>
                    ),
                    resize: true,
                  },
                ]
          }
          tableOptions={
            status?.displayMode
              ? undefined
              : {
                  renderAfterTable: () => (
                    <tfoot className="full-width-footer">
                      <TableMessage
                        disable={!areAllTracksSelected}
                        className="cursor-pointer"
                        onClick={onAddTrackToAlbum}
                      >
                        <TableItem.Text text={t("button.add")} />
                        <PiPlus />
                      </TableMessage>
                    </tfoot>
                  ),
                }
          }
        />
      )}
    </div>
  );
};

export default MusicProductPhysicalTracks;
